import React from 'react';
import PropTypes from 'prop-types';
import { selectCurrentToken } from 'slice/authSlice';
import { useSelector } from 'react-redux';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { useIntl } from 'react-intl';
import useOIDC from 'hook/useOIDC';
import useUserDetails from 'hook/useUserDetails';
import { useAuth } from 'hook/useAuth';

/**
 * Manages the automatic sign-in process for users by utilizing the OpenID Connect authentication
 * flow. It initiates a sign-in redirect if the user is not authenticated and retrieves user details
 * once authenticated.
 *
 * @component
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components to render after the authentication process completes successfully.
 *
 * @returns {React.ReactElement} Renders a loading indicator, an error message, or the child components based on the current authentication state.
 */
function OIDCAutoSignIn({ children }) {
  const { formatMessage: msg } = useIntl();
  const currentToken = useSelector(selectCurrentToken);
  const { logout } = useAuth();

  const { tokenInitialized, authError } = useOIDC();
  const shouldRetrieveUserDetails = tokenInitialized && currentToken != null;
  const { userDetailsLoading, userDetailsInitialized, hasError } =
    useUserDetails(shouldRetrieveUserDetails);

  if (authError || hasError) {
    const error = {
      message: msg({ id: 'error.openID.connect' }),
      stack: authError?.message ?? 'unknown error'
    };
    console.warn(error);
    logout(true);
    return null;
  }

  const shouldDisplayLoading = !userDetailsInitialized || userDetailsLoading;
  if (shouldDisplayLoading) {
    return (
      <IndeterminateLoading sx={{ width: '100%' }} message={msg({ id: 'loading.configuration' })} />
    );
  }

  return children;
}

OIDCAutoSignIn.propTypes = {
  children: PropTypes.node
};

export default OIDCAutoSignIn;
